import { Component, ElementRef, inject, ViewChild } from '@angular/core';
import { CdkAccordionModule } from '@angular/cdk/accordion';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Router, ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { TagsLanguageService } from '../../features/tags-language/tagsLanguage.service';
import { FaqService } from '../../features/faq/faq.service';
import { noOnlySpacesValidator } from '../../core/helpers/validator';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialog } from '../../shared/components/confirm-dialog/confirm-dialog';
import {
  noOnlySpecialCharsAlphaValidator,
  noOnlySpecialCharsValidator,
} from '../../core/helpers/validator';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ContentLoaderComponent } from '../../shared/components/content-loader-component/contentLoader.component';
import { Storage, ref, uploadBytes, getDownloadURL } from '@angular/fire/storage';


@Component({
  selector: 'faq-new-component',
  templateUrl: 'faq-new.component.html',
  styleUrl: 'faq-new.component.scss',
  imports: [
    CdkAccordionModule,
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatSelectModule,
    ContentLoaderComponent,
  ],
})
export class FaqNewComponent {
  public languages: any[] = [];
  public mainForm: FormGroup;
  faqId: any;
  readonly dialog = inject(MatDialog);
  isEditing: boolean = false;
  isSubmitting = false;
  contentLoading: boolean = false;
  videoSource: 'upload' | 'url' = 'upload';
  videoUrl: string | null = null;
  videoUploading = false;
  videoFileName: string | null = null;
  @ViewChild('videofileInput') videofileInput!: ElementRef<HTMLInputElement>;
  readonly storage = inject(Storage);

  public faqTranslations: {
    id?: number;
    langId: number;
    question: string;
    answer: string;
  }[] = [];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private tagsService: TagsLanguageService,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private faqService: FaqService
  ) {
    this.mainForm = this.fb.group({
      question: [
        null,
        [Validators.required, noOnlySpacesValidator(), noOnlySpecialCharsAlphaValidator()],
      ],
      answer: [
        null,
        [Validators.required, noOnlySpacesValidator(), noOnlySpecialCharsValidator()],
      ],
      langId: [1, Validators.required],
      videoUrl: [''],
    });
  }

  ngOnInit() {
    this.getLanguages();
    this.route.paramMap.subscribe((params) => {
      this.faqId = params.get('id');
      if (this.faqId) {
        this.contentLoading = true;
        this.getFaqById(this.faqId);
        this.mainForm.get('langId')?.patchValue(null);
      }
    });
  }

  getLanguages(): void {
    this.tagsService.getLangauges().subscribe({
      next: (res) => {
        if (res?.data) {
          this.languages = res?.data;
        }
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  getFaqById(id: any) {
    this.faqService.getFaqById(id).subscribe({
      next: (res) => {
        const faqData = res?.data;

        if (faqData) {
          // translations
          faqData.translations.forEach((translation: any) => {
            this.faqTranslations.push({
              id: translation.id,
              langId: translation.langId,
              question: translation.question,
              answer: translation.answer,
            });
          });

          if (faqData.videoUrl) {
            this.videoUrl = faqData.videoUrl;
            this.videoSource = 'url'; // show URL mode
            this.mainForm.patchValue({
              videoUrl: faqData.videoUrl,
            });
          }
        }

        this.contentLoading = false;
      },
      error: () => (this.contentLoading = false),
    });
  }

  addFaqTranslation(): void {
    const { question, answer, langId } = this.mainForm.value;

    if (this.mainForm.invalid) {
      this.toastr.warning('Please enter all the fields');
      return;
    }

    if (question === '' || question === null) {
      this.toastr.warning('Please enter valid question.');
      return;
    }

    if (answer === '' || answer === null) {
      this.toastr.warning('Please enter valid answer.');
      return;
    }

    if (langId === '' || langId === null) {
      this.toastr.warning('Please choose a valid language');
      return;
    }

    const isDuplicate = this.faqTranslations.some((t) => t.langId === langId);
    if (isDuplicate) {
      this.toastr.error('This language has already been added.');
      return;
    }
    const quesTrimmed = question.trimEnd();
    const ansTrimmed = answer.trimEnd();
    this.faqTranslations.push({
      question: quesTrimmed,
      answer: ansTrimmed,
      id: this.currId,
      langId,
    });
    this.isEditing = false;

    if (this.faqTranslations.length === this.languages.length) {
      this.mainForm.get('question')?.disable();
      this.mainForm.get('answer')?.disable();
      this.mainForm.get('langId')?.disable();
    }

    const savedVideoUrl = this.mainForm.get('videoUrl')?.value;

    this.mainForm.reset({
      videoUrl: savedVideoUrl,
      langId: 1,
    });

    this.mainForm.controls?.['question'].markAsUntouched();
    this.mainForm.controls?.['answer'].markAsUntouched();
    this.mainForm.controls?.['langId'].markAsUntouched();
    this.currId = null;
  }

  removeFaqTranslation(index: number): void {
    const dialogRef = this.dialog.open(ConfirmDialog);
    const id = this.faqTranslations[index].id;
    if (!id) {
      dialogRef.afterClosed().subscribe((result) => {
        if (result === true) {
          this.faqTranslations.splice(index, 1);
        }
      });
    } else {
      dialogRef.afterClosed().subscribe((result) => {
        if (result === true) {
          this.faqService.deleteFaqTranslation(id).subscribe({
            next: (res) => {
              this.faqTranslations.splice(index, 1);
              this.mainForm.get('question')?.enable();
              this.mainForm.get('answer')?.enable();
              this.mainForm.get('langId')?.enable();
            },
            error: (err) => {
              console.log(err);
            },
          });
        }
      });
    }
  }

  currId: any;
  editFaqTranslation(index: number): void {
    if (this.isEditing) {
      this.addFaqTranslation();
    }
    this.isEditing = true;
    const selected = this.faqTranslations[index];
    this.mainForm.get('question')?.enable();
    this.mainForm.get('answer')?.enable();
    this.mainForm.get('langId')?.enable();

    this.currId = selected.id;

    this.faqTranslations.splice(index, 1);
    this.mainForm.patchValue({
      question: selected.question,
      langId: selected.langId,
      answer: selected.answer,
    });
    this.mainForm.get('question')?.markAsTouched();
    this.mainForm.get('langId')?.markAsTouched();
    this.mainForm.get('answer')?.markAsTouched();
    this.scrollUp(`translation`);
  }

  scrollUp(elementId: string): void {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }

  getLanguageName(langId: number): string {
    const lang = this.languages.find((l) => l.id === langId);
    return lang ? lang.name : `Lang ID: ${langId}`;
  }

  get sortedTranslations() {
    return this.faqTranslations
      ?.slice()
      .sort((a, b) => (a.langId === 1 ? -1 : b.langId === 1 ? 1 : 0));
  }

  isLangUsed(langId: number): boolean {
    return this.faqTranslations?.some((t) => t.langId === langId);
  }

  onSaveFaq() {
    if (this.isEditing) {
      this.addFaqTranslation();
    }

    if (!this.faqTranslations.some(t => t.langId === 1)) {
      this.toastr.error('FAQ in English is mandatory');
      return;
    }

    const payload = {
      position: 1,
      videoUrl: this.mainForm.value.videoUrl || null, // ✅ optional
      translations: this.faqTranslations,
    };

    this.isSubmitting = true;

    const req$ = this.faqId
      ? this.faqService.updateFaq(payload, this.faqId)
      : this.faqService.createFaq(payload);

    req$.subscribe({
      next: () => {
        this.toastr.success(this.faqId ? 'FAQ updated successfully' : 'FAQ created successfully');
        this.router.navigate(['/faq']);
      },
      error: () => (this.isSubmitting = false),
    });
  }

  onCancel() {
    this.router.navigate(['/faq']);
  }

setVideoSource(source: 'upload' | 'url') {
  if (this.videoSource === source) return;

  this.videoSource = source;

  if (source === 'url') {
    this.clearVideoUpload();
  }
}

  async onVideoUpload(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    const allowedTypes = ['video/mp4', 'video/quicktime'];
    const maxSize = 300 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      this.toastr.error('Choose MP4 or MOV format');
      input.value = '';
      return;
    }

    if (file.size > maxSize) {
      this.toastr.error('Video must be less than 300MB');
      input.value = '';
      return;
    }

    this.videoUploading = true;

    const filePath = `faq-video/${Date.now()}_${file.name}`;
    const fileRef = ref(this.storage, filePath);

    try {
      await uploadBytes(fileRef, file);
      const downloadUrl = await getDownloadURL(fileRef);

      // ✅ SET URL EVERYWHERE
      this.videoUrl = downloadUrl;
      this.videoFileName = file.name;

      // 🔥 SHOW URL IN INPUT
      this.mainForm.patchValue({
        videoUrl: downloadUrl,
      });
    } catch (err) {
      this.toastr.error('Video upload failed');
    } finally {
      this.videoUploading = false;
    }
  }


  clearVideoUpload(): void {
    this.videoFileName = null;
    this.videoUrl = null;

    this.mainForm.patchValue({ videoUrl: '' });

    if (this.videofileInput) {
      this.videofileInput.nativeElement.value = '';
    }
  }
}
